import { useMemo, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { ConstellationStarDto, HubPlanetDto } from '@aiai/shared';
import { fetchConstellation } from '../api';
import { qk } from '../api/queryKeys';
import { formatRelative } from '../lib/format';
import { screenFont } from './camera';
import {
  CONSTELLATION_LABEL_PX, CONSTELLATION_SCALE as SCALE, constellationFigure, constellationLabelAnchor,
  constellationLabelText, constellationName, constellationShown, litCount,
} from './constellationGen';

const DAY_MS = 24 * 60 * 60 * 1000;
/**
 * A world circle of radius `r` drawn between `minPx` and `maxPx` on screen at any zoom: it grows
 * with the camera inside that band and is counter-scaled (`--lk` = 1 / camera scale) outside it —
 * the ship glyph's trick, so a zoomed-in star never swells into a blob.
 */
const screenRadius = (r: number, minPx: number, maxPx: number): CSSProperties => ({
  transformBox: 'fill-box',
  transformOrigin: 'center',
  transform: `scale(clamp(calc(var(--lk, 1) * ${minPx / r}), 1, calc(var(--lk, 1) * ${maxPx / r})))`,
});
/** Star sizes (world radius, then the on-screen band): the head is always the largest. */
const HEAD = { r: 2.4, min: 1.8, max: 4.5 };
const star = (priority: number) => ({ r: 1.1 + 0.2 * priority, min: 0.9 + 0.1 * priority, max: 2 + 0.45 * priority });
const HINT = { r: 1.1, min: 0.9, max: 2 };

export interface ConstellationProps {
  planet: HubPlanetDto;
  anchor: { x: number; y: number };
  /**
   * Zoomed in past fit: fetch the stars and show the text caption. Otherwise both wait for hover or
   * focus (the caption invisibly, through CSS, no render); the aria-label always carries it.
   */
  detailed: boolean;
  onOpenIssue: (planet: HubPlanetDto, issueId: string) => void;
  /** The ring around the head (newest) star, for the universe's "something shipped" pulse. */
  headRef?: (el: SVGCircleElement | null) => void;
}

export function Constellation({
  planet, anchor, detailed, onOpenIssue, headRef,
}: ConstellationProps) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const lit = litCount(planet.shippedCount);
  const wantDetails = detailed || hovered || focused;
  const { data } = useQuery({
    // Keyed on the count: a live merge raises shippedCount, which refetches so the head names the
    // new issue; the previous stars stay up meanwhile (no flash).
    queryKey: qk.constellation(planet.id, planet.shippedCount),
    queryFn: () => fetchConstellation(planet.id, planet.workspace.slug),
    enabled: wantDetails && lit > 0,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
  const figure = useMemo(() => constellationFigure(planet.id), [planet.id]);
  const labelAt = useMemo(() => constellationLabelAnchor(planet.id, planet.shippedCount), [planet.id, planet.shippedCount]);

  if (lit === 0) return null;

  const name = constellationName(planet.id);
  // Lit = the head plus the next stars in growth order; hints hang off lit stars (outline only).
  const { lit: litIdx, hints } = constellationShown(figure, lit);
  const shown = new Set([...litIdx, ...hints]);

  const aria = `${name} — ${planet.code ?? planet.name} constellation, ${planet.shippedCount} shipped`;
  const pt = (i: number) => ({ x: figure.points[i].x * SCALE, y: figure.points[i].y * SCALE });

  return (
    <g
      role="group"
      tabIndex={0}
      aria-label={aria}
      // The browser's focus box would frame the whole figure; focus anywhere inside (the group or a
      // star) inks the lines and the name instead.
      className="group/c outline-none"
      transform={`translate(${anchor.x} ${anchor.y})`}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      {figure.edges.map(([a, b]) => {
        if (!shown.has(a) || !shown.has(b)) return null;
        const toHint = b >= lit;
        return (
          <line
            key={`${a}-${b}`}
            data-edge={toHint ? 'hint' : 'lit'}
            x1={pt(a).x} y1={pt(a).y} x2={pt(b).x} y2={pt(b).y}
            className={`stroke-line-strong ${toHint ? '' : 'group-focus-within/c:stroke-ink'}`} strokeWidth={1}
            vectorEffect="non-scaling-stroke" opacity={toHint ? 0.4 : 1}
          />
        );
      })}
      {hints.map((i) => (
        <circle key={`u${i}`} data-hint data-i={i} cx={pt(i).x} cy={pt(i).y} r={HINT.r} style={screenRadius(HINT.r, HINT.min, HINT.max)}
          className="fill-surface-2 stroke-line-strong" strokeWidth={1} vectorEffect="non-scaling-stroke" />
      ))}
      {litIdx.map((i, k) => {
        const s: ConstellationStarDto | undefined = data?.stars[k];
        const { x, y } = pt(i);
        const size = k === 0 ? HEAD : star(s?.priority ?? 1);
        const shape = { cx: x, cy: y, r: size.r, style: screenRadius(size.r, size.min, size.max) };
        if (!s) {
          return <circle key={`l${i}`} data-lit data-i={i} {...shape} className="fill-ink" opacity={k === 0 ? 1 : 0.7} />;
        }
        const label = `${s.code ? s.code + ' ' : ''}${s.title}, shipped ${formatRelative(s.shippedAt)}`;
        const fresh = k === 0 && Date.now() - new Date(s.shippedAt).getTime() < DAY_MS;
        const open = () => onOpenIssue(planet, s.issueId);
        const onKey = (e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            open();
          }
        };
        return (
          <circle
            key={`l${i}`}
            data-lit
            data-i={i}
            role="button"
            // Tab stops only once the figure has focus: Tab enters the group first, then its stars,
            // so a zoomed-in universe doesn't put every star ahead of the planets.
            tabIndex={focused ? 0 : -1}
            aria-label={label}
            {...shape}
            opacity={k === 0 ? 1 : 0.55 + 0.15 * s.priority}
            // SVG gets no outline in Firefox: a keyboard-focused star draws its own ring.
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
            className={`${fresh ? 'fill-ok' : 'fill-ink'} cursor-pointer outline-none focus-visible:stroke-ink`}
            onClick={open}
            onKeyDown={onKey}
          >
            <title>{label}</title>
          </circle>
        );
      })}
      <circle ref={headRef} data-constellation-head aria-hidden="true" cx={pt(0).x} cy={pt(0).y} r={4}
        style={screenRadius(4, 5, 8)} fill="none" className="stroke-ok" opacity={0} />
      {/* Always in the DOM so hover and focus reveal it through CSS; data-caption mirrors the state.
          aria-hidden: the group's aria-label already carries the caption. */}
      <text x={labelAt.x} y={labelAt.y} textAnchor="middle" dominantBaseline="hanging" aria-hidden="true"
        data-caption={wantDetails ? 'shown' : 'on-demand'}
        className={`pointer-events-none fill-ink-muted transition-opacity group-focus-within/c:fill-ink group-focus-within/c:underline ${
          wantDetails ? '' : 'opacity-0 group-hover/c:opacity-100 group-focus-within/c:opacity-100'}`}
        style={screenFont(CONSTELLATION_LABEL_PX)}>
        {constellationLabelText(planet.id, planet.shippedCount)}
      </text>
    </g>
  );
}
